import { createSlice,createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_BASE_URL from "../../apis/api"
import {API_INTG_URL} from "../../apis/api"; 

const api = axios.create({
  baseURL: API_INTG_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ✅ Axios interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.warn("⚠️ Access token expired. Attempting to refresh...");
      try {
        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) throw new Error("No refresh token available");
        const response = await api.post("/api/auth/token/refresh/", {
          refresh: refreshToken,
        });
        const { access } = response.data;
        localStorage.setItem("access_token", access);
        setAuthToken(access);
        // Retry the failed request with the new token
        error.config.headers["Authorization"] = `Bearer ${access}`;
        return api.request(error.config);
      } catch (refreshError) {
        console.error("❌ Token refresh failed. Logging out...");
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/"; // Redirect to login
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// ✅ Function to set the Authorization token
export const setAuthToken = (token) => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  };

  
export const STATUSES = Object.freeze({
  LOADING: "loading",
  IDLE: "idle",
  ERROR: "error",
});



// ✅ Thunk to fetch emails (without body) for a case
export const fetchEmailsData = createAsyncThunk(
  "emails/fetchEmailsData",
  async ({ caseNumber, email,thread_id }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found. Please log in.");

      setAuthToken(token); // Set Authorization token

      const response = await api.get(`/api/supplychain/cases/${caseNumber}/email/list_by_thread/`, {
        params: { email,thread_id }, // Pass email as query parameter
      });

      console.log("✅ Emails (without body) fetched:", response.data);

      return { caseNumber, emails: response.data.emails }; // Ensure consistent structure
    } catch (error) {
      console.error("❌ Error fetching emails (without body):", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


// ✅ Thunk to fetch email body by caseNumber and threadId
export const fetchEmailBody = createAsyncThunk(
  "emails/fetchEmailBody",
  async ({ caseNumber, email, thread_id, emailId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found. Please log in.");

      setAuthToken(token); // Set Authorization token

      // Fetch email bodies for the caseNumber
      const response = await api.get(`/api/supplychain/cases/${caseNumber}/email/list_bodies_by_thread/`,{
        params: { email,thread_id }, // Pass email as query parameter
      });

      console.log("✅ Email bodies fetched:", response.data);

      // Extract email body for the specific threadId & emailId
      const emailBodies = response.data.emails || {}; // Ensure default empty object
      const emailBody = emailBodies.find((email) => email.id === emailId);

      if (!emailBody) throw new Error("Email body not found");

      return { caseNumber, thread_id, emailId, body: emailBody.body };
    } catch (error) {
      console.error("❌ Error fetching email body:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ✅ Thunk to send draft email
export const sendDraftEmail = createAsyncThunk(
  "emails/sendDraftEmail",
  async ({ caseNumber, email, thread_id, reply_text }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found. Please log in.");

      setAuthToken(token); // Set Authorization token

      // Step 1: Fetch draft email details to extract draft_id
      const draftDetailsResponse = await api.get(
        `/api/supplychain/cases/email/draft/detail/${caseNumber}/`,
        {
          params: { email }, // Pass email as query parameter
        }
      );

      const draftDetails = draftDetailsResponse.data;
      const draft_id = draftDetails.draft_id; // Extract draft_id

      if (!draft_id) {
        throw new Error("Draft ID not found in the draft details.");
      }


      // Step 2: Send the draft email
      const sendDraftResponse = await api.post(
        `/api/supplychain/cases/email/draft/send/`,
        {
          case_number: caseNumber,
          draft_id,
          thread_id,
          reply_text,
        },
        {
          params: { email }, // Pass email as query parameter
        }
      );

      console.log("✅ Draft email sent successfully:", sendDraftResponse.data);

      return sendDraftResponse.data; // Return the response data
    } catch (error) {
      console.error("❌ Error sending draft email:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


// ✅ Thunk to create a draft email
export const createDraftEmail = createAsyncThunk(
  "emails/createDraftEmail",
  async ({ caseNumber, replyText, email }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found. Please log in.");

      // ✅ Prepare request payload
      const payload = JSON.stringify({
        case_number: caseNumber,
        reply_text: replyText
      });

      // ✅ Send POST request to create draft email
      const response = await api.post(
        `/api/supplychain/cases/email/reply/?email=${email}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log(`✅ Draft email created for case ${caseNumber}:`, response.data);

      return response.data; // ✅ Return the created draft data
    } catch (error) {
      console.error(`❌ Failed to create draft email for case ${caseNumber}:`, error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


// ✅ Thunk to fetch an existing draft email
export const getDraftEmail = createAsyncThunk(
  "emails/getDraftEmail",
  async ({ caseNumber, email }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found. Please log in.");

      // ✅ Send GET request to fetch draft email details
      const response = await api.get(
        `/api/supplychain/cases/email/draft/detail/${caseNumber}/?email=${email}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log(`✅ Draft email retrieved for case ${caseNumber}:`, response.data);

      return response.data; // ✅ Return fetched draft email data
    } catch (error) {
      console.error(`❌ Failed to fetch draft email for case ${caseNumber}:`, error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


// Thunk to persist read status to backend
export const setReadStatusThunk = (threadId, emailId) => async (dispatch, getState) => {
  const email = getState().emails.data[threadId]?.find((e) => e.emailId === emailId);
  if (!email || email.readStatus === "read") return; // Skip if already read

  try {
    // Update backend
    const updatedEmails = getState().emails.data[threadId].map((e) =>
      e.emailId === emailId ? { ...e, readStatus: "read" } : e
    );
    await axios.patch(`${API_BASE_URL}/caseLogs/${threadId}`, { emaillogs: updatedEmails });

    // Dispatch action to update Redux state
    dispatch(setReadStatus({ threadId, emailId }));
  } catch (error) {
    console.error("Failed to update read status:", error);
  }
};

// Thunk for email status update
export const updateEmailStatusThunk = (threadId, emailId, status) => async (dispatch, getState) => {
  const email = getState().emails.data[threadId]?.find((e) => e.emailId === emailId);
  if (!email) return;

  try {
    // Update the backend
    const updatedEmails = getState().emails.data[threadId].map((e) =>
      e.emailId === emailId ? { ...e, Status: status } : e
    );
    await axios.patch(`${API_BASE_URL}/caseLogs/${threadId}`, { emaillogs: updatedEmails });

    // Dispatch the Redux update
    dispatch(updateEmailStatus({ threadId, emailId, status }));
  } catch (error) {
    console.error("Failed to update email status:", error);
  }
};


// Thunk to toggle email tag and persist to backend
export const toggleEmailTag = (threadId, emailId, tagged) => async (dispatch, getState) => {
  const email = getState().emails.data[threadId]?.find((e) => e.emailId === emailId);
  if (!email) return;

  try {
    // Toggle the tag state
    const updatedEmails = getState().emails.data[threadId].map((e) =>
      e.emailId === emailId ? { ...e, tagged: !tagged } : e
    );
    await axios.patch(`${API_BASE_URL}/caseLogs/${threadId}`, { emaillogs: updatedEmails });

    // Dispatch action to update Redux state
    dispatch(toggleTag({ threadId, emailId }));
  } catch (error) {
    console.error("Failed to toggle email tag:", error);
  }
};

// Thunk to toggle email pin and persist to backend
export const toggleEmailPin = (threadId, emailId, isPinned) => async (dispatch, getState) => {
  const email = getState().emails.data[threadId]?.find((e) => e.emailId === emailId);
  if (!email) return;

  try {
    const updatedEmails = getState().emails.data[threadId].map((e) =>
      e.emailId === emailId ? { ...e, pinned: !isPinned } : e
    );

    // Persist updated state to backend
    await axios.patch(`${API_BASE_URL}/caseLogs/${threadId}`, { emaillogs: updatedEmails });

    // Dispatch local Redux update
    dispatch(updatePinState({ threadId, emailId }));
  } catch (error) {
    console.error("Failed to update pin state:", error);
  }
};
const initialState = {
  emails: {}, // Emails grouped by threadId { threadId: [emails] }
  emailbodies:[],
  status: STATUSES.IDLE,
  error: null, // To store error messages
  mailOpen: false,
  currentMail: null,
  pinnedEmails: [], // Array to track multiple pinned emails
};

// Slice definition
export const emailsSlice = createSlice({
  name: "emails",
  initialState,
  reducers: {
    setMailClose: (state) => {
      state.currentMail = null;
      state.mailOpen = false;
    },
    setData: (state, action) => {
      const { threadId, emails } = action.payload;
      state.data[threadId] = emails.map((email) => ({
        ...email,
        readStatus: email.readStatus || "unread",
        tagged: email.tagged || false,
        pinned: email.pinned || false,
      }));
    },
    setStatus: (state, action) => {
      if (typeof action.payload === "object") {
        state.status = action.payload.status;
        state.error = action.payload.error || null;
      } else {
        state.status = action.payload;
        state.error = null;
      }
    },
    setReadStatus: (state, action) => {
      const { threadId, emailId } = action.payload;
      const email = state.data[threadId]?.find((e) => e.emailId === emailId);
      if (email) email.readStatus = "read";
    },
    toggleTag: (state, action) => {
      const { threadId, emailId } = action.payload;
      const email = state.data[threadId]?.find((e) => e.emailId === emailId);
      if (email) email.tagged = !email.tagged;
    },
    updateEmailStatus: (state, action) => {
      const { threadId, emailId, status } = action.payload;
      const email = state.data[threadId]?.find((e) => e.emailId === emailId);
      if (email) {
        email.Status = status;
      }
    },
    updatePinState: (state, action) => {
      const { threadId, emailId } = action.payload;
      const email = state.data[threadId]?.find((e) => e.emailId === emailId);
      if (email) {
        email.pinned = !email.pinned;
        if (email.pinned) {
          state.pinnedEmails.push(email.emailId);
        } else {
          state.pinnedEmails = state.pinnedEmails.filter((id) => id !== emailId);
        }
        console.log("UpdatePinState: Pinned Emails", state.pinnedEmails);
      }
    },
    setPinnedEmail: (state, action) => {
      const { emailId } = action.payload;
      if (!state.pinnedEmails.includes(emailId)) {
        state.pinnedEmails.push(emailId);
      }
    },
    removePinnedEmail: (state, action) => {
      const { emailId } = action.payload;
      state.pinnedEmails = state.pinnedEmails.filter((id) => id !== emailId);
    },
    setMailOpen: (state, action) => {
      state.mailOpen = action.payload;
    },
    setCurrentMail: (state, action) => {
      state.currentMail = action.payload;
    },
    updateEmailMetadata: (state, action) => {
      const { threadId, emailId, amendedBy, lastUpdated } = action.payload;
      const email = state.data[threadId]?.find((e) => e.emailId === emailId);
      if (email) {
        email.amendedBy = amendedBy;
        email.lastUpdated = lastUpdated;
      }
    },
    setEmailBody: (state, action) => {
      const { threadId, emailId, emailBody } = action.payload;
      const email = state.data[threadId]?.find((e) => e.emailId === emailId);
      if (email) {
        email.emailBody = emailBody; // Update the email body in the state
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Handle fetching an existing draft email
      .addCase(getDraftEmail.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getDraftEmail.fulfilled, (state, action) => {
        state.currentMail = action.payload; // ✅ Store fetched draft
        state.status = "succeeded";
      })
      .addCase(getDraftEmail.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(fetchEmailsData.pending, (state) => {
        state.status = STATUSES.LOADING;
        state.error = null;
      })
      .addCase(fetchEmailsData.fulfilled, (state, action) => {
        const { caseNumber, emails } = action.payload;
        state.emails[caseNumber] = emails; // Store emails under caseNumber key
        state.status = STATUSES.IDLE;
      })
      .addCase(fetchEmailsData.rejected, (state, action) => {
        state.status = STATUSES.ERROR;
        state.error = action.payload || "Failed to fetch emails.";
      });
  },
});

// Actions
export const {
  setData,
  setStatus,
  setReadStatus,
  toggleTag,
  togglePin,
  setPinnedEmail,
  updatePinState,
  removePinnedEmail,
  setMailOpen,
  setMailClose,
  setCurrentMail,
  updateEmailMetadata,
  updateEmailStatus,
  setEmailBody
} = emailsSlice.actions;



export default emailsSlice.reducer;